import { useState } from "react";
import { Link } from "react-router";
import { ClickChart } from "../components/ClickChart";
import { MARKETING_URL } from "../lib/api";
import { PrimaryLink } from "../components/PrimaryLink";
import {
  Alert,
  Card,
  CardHeader,
  LoadingBlock,
  PageHeader,
  Stat,
  StatGroup,
  buttonStyles,
} from "../components/ui";
import { formatCount, formatMoney, formatRate } from "../lib/money";
import { useLinks, useMedia, useOverview, usePromotions } from "../lib/queries";
import type { Program } from "../lib/types";
import { useTranslation } from "react-i18next";

const WINDOWS = [7, 30, 90];

/**
 * One funnel step as a percentage of the one before it.
 *
 * Null where the previous step is zero, which the caller renders as no hint at
 * all: a rate out of nothing is not a rate, and "0%" under a brand new account
 * reads as a verdict rather than as an empty table.
 */
function rate(step: number, previous: number, suffix: string): string | undefined {
  if (previous <= 0) return undefined;
  const percent = (step / previous) * 100;
  // One decimal below 10%, none above: click-to-signup is routinely under 1%
  // and "0%" would erase the difference between a good month and a dead one,
  // while "45.0%" of signups converting is false precision.
  return `${percent < 10 ? percent.toFixed(1) : Math.round(percent)}% ${suffix}`;
}

export function OverviewPage() {
  const { t } = useTranslation("overview");
  const [days, setDays] = useState(30);
  const { data, isPending, error } = useOverview(days);
  const links = useLinks(false);

  if (isPending) return <LoadingBlock />;
  if (error) return <Alert>{error.message}</Alert>;

  const { affiliate, program, balance } = data;

  /*
   * An account nothing has happened to yet.
   *
   * Worth detecting rather than letting the ordinary layout render, because
   * the ordinary layout for this account is four zeros, three zeros and an
   * empty chart — eight pieces of furniture that answer a question nobody has
   * asked yet and no answer to the one they have, which is "what do I do".
   *
   * Keyed on activity and not on the balance: somebody who has been paid
   * everything they earned is back to zero money and is emphatically not new.
   */
  const isNew = balance.clicks === 0 && balance.referrals === 0 && balance.paidMicros === 0;

  const shortfall = program.minPayoutMicros - balance.payableMicros;
  // Assumed true while unknown, so the first-run copy does not tell somebody to
  // create a link the request simply failed to load.
  const hasLink = links.error || !links.data ? true : links.data.some((link) => !link.archived);

  return (
    <>
      <PageHeader
        title={t("hello", { name: affiliate.displayName || t("there") })}
        /*
         * The one action this screen exists to cause, in the header rather than
         * under the fold. It sat below the chart, which put the thing somebody
         * came here to do after three panels of numbers and left it looking like
         * a footnote — and on a phone, off the screen entirely.
         */
        action={
          /* Pointless on an account with no traffic: it leads to a screen of
             the same zeros this one is already showing. */
          isNew ? undefined : (
            <Link className={buttonStyles("secondary", "md")} to="/traffic">
              {t("seeWorking")}
            </Link>
          )
        }
      />

      {/* An account that is not active is the first thing on the page, because
          everything below it is a number that has stopped moving. The reason is
          shown when there is one: somebody whose earnings stopped is owed one. */}
      {affiliate.status !== "active" && (
        <Alert tone={affiliate.status === "pending" ? "warn" : "error"}>
          {affiliate.status === "pending"
            ? t("pending")
            : (affiliate.statusReason ?? t("inactive"))}
        </Alert>
      )}

      {!program.acceptingReferrals && affiliate.status === "active" && (
        <Alert tone="warn">
          {t("closed")}
        </Alert>
      )}

      <PrimaryLink links={links.data} pending={links.isPending} failed={Boolean(links.error)} />

      {/*
        Directly under the link, and above the money, for the reason the link is
        there at all: these are the two things an affiliate hands to somebody
        else, and the balance is their consequence. It used to sit last, below
        the click chart, which put the one screen element a new affiliate could
        act on beneath four figures they cannot.
      */}
      <PromoteCard />

      {isNew && <FirstRun program={program} hasLink={hasLink} />}

      {/*
       * The money, as one panel rather than four boxes — see `StatGroup` for why
       * a life cycle is a divided panel and not a grid of cards.
       *
       * Left to right is the order a commission actually moves in. `Reversed` is
       * deliberately not among them: it is not a stage money passes through, and
       * a fifth column here would imply it sums with the other four. It appears
       * on the commissions table instead, against the row it happened to.
       */}
      <StatGroup>
        <Stat
          boxed={false}
          size="lg"
          label={t("held")}
          value={formatMoney(balance.pendingMicros)}
          hint={t("heldHint", { count: program.holdDays })}
          tone={balance.pendingMicros === 0 ? "muted" : "default"}
        />
        <Stat
          boxed={false}
          size="lg"
          label={t("cleared")}
          value={formatMoney(balance.payableMicros)}
          hint={t("clearedHint")}
          tone={balance.payableMicros === 0 ? "muted" : "default"}
        />
        <Stat
          boxed={false}
          size="lg"
          label={t("beingPaid")}
          value={formatMoney(balance.inPayoutMicros)}
          hint={t("beingPaidHint")}
          tone={balance.inPayoutMicros === 0 ? "muted" : "default"}
        />
        <Stat
          boxed={false}
          size="lg"
          label={t("paid")}
          value={formatMoney(balance.paidMicros)}
          hint={t("paidHint")}
          tone={balance.paidMicros === 0 ? "muted" : "default"}
        />
      </StatGroup>

      {/*
       * The terms, as a legend under the numbers they govern rather than as a
       * paragraph under the greeting.
       *
       * They were three sentences at the top of the page, which is a thing to
       * read before the thing you came for. But deleting them was not an
       * option: this is the **only** place the rate and the cap appear anywhere
       * an affiliate can see, and the sign-in panel deliberately omits the cap
       * on the argument that it is stated here. Remove it from both and the
       * programme has an unpublished term.
       *
       * So: one line, in the same voice as a column header, attached to the row
       * it describes. Somebody scanning their balance does not read it;
       * somebody wondering why a commission is £X looks just below the figure
       * and finds the arithmetic.
       */}
      <div className="-mt-3 flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
        <p className="text-[12px] text-fg-subtle">
          {t("terms", { rate: formatRate(program.commissionBps), cap: formatMoney(program.commissionCapMicros) })}
        </p>

        {/*
         * What to do about the cleared figure, next to it.
         *
         * "Cleared: $24.00" is a number somebody wants to act on, and the
         * action was two clicks away with nothing on this screen saying whether
         * it was even available. Now the screen answers the question the figure
         * raises: either the payout is there to request, or it says exactly how
         * much more is needed — which is the difference between a dashboard
         * that reports and one that tells you where you are.
         */}
        {balance.payableMicros > 0 &&
          (shortfall <= 0 ? (
            <Link
              className="text-[12px] text-accent transition-colors duration-200 ease-smooth hover:underline"
              to="/payouts"
            >
              {t("request", { amount: formatMoney(balance.payableMicros) })}
            </Link>
          ) : (
            <p className="text-[12px] text-fg-subtle">
              {t("shortfall", { amount: formatMoney(shortfall), minimum: formatMoney(program.minPayoutMicros) })}
            </p>
          ))}
      </div>

      {/*
       * The funnel and the chart, and both are hidden on an account nothing has
       * happened to.
       *
       * They are a different question from the row above — that is money, this
       * is people — and on day one neither question has an answer. Three zeros
       * over an empty chart is furniture: it fills the screen a new affiliate
       * forms their opinion on with six pieces of nothing, directly under a
       * panel that just finished telling them what to do instead.
       *
       * They come back the moment a single click lands, which is the point at
       * which they start saying something.
       */}
      {!isNew && (
      <div className="grid grid-cols-3 divide-x divide-line overflow-hidden rounded-lg border border-line bg-surface shadow-xs">
        <Stat boxed={false} label={t("clicks")} value={formatCount(balance.clicks)} hint={t("clicksHint")} />
        {/*
         * Each step carries the rate from the step before it, because that is
         * the number an affiliate can actually act on: 1,739 clicks is a fact
         * about last month, and "0.6% of them signed up" is a fact about the
         * audience they are sending. The count alone invites nobody to change
         * anything.
         *
         * Omitted rather than shown as 0% where the previous step is zero — a
         * rate out of nothing is not a rate, and "0%" beside a new account
         * reads as failure rather than as no data yet.
         */}
        <Stat
          boxed={false}
          label={t("signups")}
          value={formatCount(balance.referrals)}
          hint={rate(balance.referrals, balance.clicks, t("ofClicks"))}
        />
        <Stat
          boxed={false}
          label={t("converted")}
          value={formatCount(balance.conversions)}
          hint={rate(balance.conversions, balance.referrals, t("ofSignups"))}
        />
      </div>
      )}

      {!isNew && (
      <Card>
        <CardHeader
          title={t("clicksTitle")}
          description={t("clicksDescription")}
          action={
            <div className="flex gap-1" role="group" aria-label={t("period")}>
              {WINDOWS.map((option) => (
                <button
                  key={option}
                  type="button"
                  onClick={() => setDays(option)}
                  className={
                    option === days
                      ? buttonStyles("outline", "sm")
                      : buttonStyles("ghost", "sm")
                  }
                >
                  {t("daysShort", { count: option })}
                </button>
              ))}
            </div>
          }
        />
        <ClickChart series={data.clicksByDay} />
      </Card>
      )}

      {program.termsUrl && (
        <p className="text-[13px]">
          <a
            className="text-accent transition-colors duration-200 ease-smooth hover:text-accent-hover"
            href={program.termsUrl}
            target="_blank"
            rel="noreferrer"
          >
            {t("programTerms")}
          </a>
        </p>
      )}
    </>
  );
}

/**
 * What a brand new affiliate is told, in place of a wall of zeros.
 *
 * The screen this replaces showed four balances of $0.00, three counts of 0,
 * and an empty chart. All of it true, none of it useful: somebody who has just
 * joined does not need to be told they have earned nothing — they need to know
 * what happens next, and whether they have to do anything before their link
 * works.
 *
 * Three steps and no more. The temptation with a first run is to explain the
 * whole programme, and the whole programme is on the sign-in page and in the
 * terms; what belongs here is the shortest true path from "I just joined" to
 * "I have been paid".
 *
 * The hold window and the minimum come from the programme rather than being
 * written into the copy, so an operator changing either on `/admin/terms` does
 * not leave this paragraph quietly lying to every new affiliate.
 */
/**
 * One card pointing at everything an affiliate promotes us with.
 *
 * This was two — the offers listed here, and a strip of images beside them —
 * and both were the same argument arriving twice: here is a thing to share,
 * the rest is on another page. Now that the page exists, restating its contents
 * above the balance is a section the overview does not need. The overview's job
 * is the link and what it has earned.
 *
 * The thumbnails stay because a picture of a media kit says "there is something
 * here" better than a sentence does, and they cost one row whatever is behind
 * them. What is gone is the offer rows: their number was an operator's decision
 * and this screen should not grow with it.
 *
 * Renders nothing when there is nothing behind the link — no campaign, no
 * images, or no marketing origin configured.
 */
function PromoteCard() {
  const { t } = useTranslation("overview");
  const { data: promotions } = usePromotions();
  const { data: media } = useMedia();

  const offers = promotions?.length ?? 0;
  const images = MARKETING_URL ? (media?.length ?? 0) : 0;
  if (offers === 0 && images === 0) return null;

  /* What is actually behind the link, said in its own terms. */
  const has = [
    offers > 0 ? t("promote.offers", { count: offers }) : null,
    images > 0 ? t("promote.images", { count: images }) : null,
  ].filter(Boolean);

  return (
    <Card>
      <CardHeader
        title={t("promote.title")}
        description={t("promote.description", { has: has.join(t("promote.and")) })}
        action={
          <Link to="/promote" className="text-sm text-accent hover:underline">
            {t("promote.open")}
          </Link>
        }
      />
      {images > 0 && (
        <div className="grid grid-cols-3 gap-3 px-5 py-4">
          {media!.slice(0, 3).map((image) => (
            <Link
              key={image.name}
              to="/promote"
              className="flex aspect-[16/10] items-center justify-center overflow-hidden rounded-md border border-line bg-raised p-2 transition hover:border-line-strong"
            >
              <img
                src={`${MARKETING_URL}/promo/${image.name}`}
                alt=""
                width={image.width}
                height={image.height}
                loading="lazy"
                className="max-h-full max-w-full object-contain"
              />
            </Link>
          ))}
        </div>
      )}
    </Card>
  );
}

function FirstRun({ program, hasLink }: { program: Program; hasLink: boolean }) {
  const { t } = useTranslation("overview");
  const steps = [
    {
      title: hasLink ? t("firstRun.share.title") : t("firstRun.create.title"),
      /*
       * Worded off the card above rather than assuming one is there. Every
       * affiliate is given a link on first sign-in, so "copy it from above" is
       * almost always right — but somebody who archived theirs was being told
       * to copy a link from a card that says they have none, which is the kind
       * of small contradiction that makes a reader distrust the rest of it.
       */
      body: hasLink ? t("firstRun.share.body") : t("firstRun.create.body"),
    },
    {
      title: t("firstRun.pays.title"),
      body: t("firstRun.pays.body", { rate: formatRate(program.commissionBps), cap: formatMoney(program.commissionCapMicros) }),
    },
    {
      title: t("firstRun.clears.title"),
      body: t("firstRun.clears.body", { count: program.holdDays, minimum: formatMoney(program.minPayoutMicros) }),
    },
  ];

  return (
    <Card>
      <CardHeader
        title={t("firstRun.title")}
        description={t("firstRun.description")}
      />
      <ol className="divide-y divide-line">
        {steps.map((step, index) => (
          <li key={step.title} className="flex gap-3 px-4 py-3">
            {/* Numbered, because these are sequential and a bulleted list of
                three would not say so. Tabular so the digits align down the
                column rather than drifting with the text. */}
            <span
              className="mt-0.5 inline-flex size-5 shrink-0 items-center justify-center rounded-full bg-raised text-[11px] tabular-nums text-fg-muted"
              aria-hidden="true"
            >
              {index + 1}
            </span>
            <div className="min-w-0 space-y-0.5">
              <p className="text-[13px] font-medium text-fg">{step.title}</p>
              <p className="text-[13px] text-fg-muted">{step.body}</p>
            </div>
          </li>
        ))}
      </ol>
    </Card>
  );
}
